import { useRef, useState } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, formatApiError } from "../../lib/adminApi";

export const ImageField = ({ value, onChange, testId }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const pick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const url = await uploadImage(file);
            onChange(url);
            toast.success("Imagen subida");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div>
            <label className="block text-sm font-semibold text-brand-ink">Imagen principal</label>
            <div className="mt-1.5 flex items-center gap-3">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-black/[0.03]">
                    {value ? (
                        <img data-testid={`${testId}-preview`} src={value} alt="Vista previa" className="h-full w-full object-contain" />
                    ) : (
                        <span className="text-[10px] font-medium text-brand-muted">Sin imagen</span>
                    )}
                </div>
                <div className="flex-1">
                    <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
                    <button
                        type="button"
                        data-testid={`${testId}-upload`}
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand-ink px-5 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-60"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {uploading ? "Subiendo..." : "Subir imagen"}
                    </button>
                    <input
                        data-testid={`${testId}-url`}
                        type="url"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="o pega una URL https://..."
                        className={`${inputCls} mt-2 text-sm`}
                    />
                </div>
            </div>
        </div>
    );
};

export const MultiImageField = ({ value = [], onChange, testId, label = "Fotos adicionales (galería)" }) => {
    const inputRef = useRef(null);
    const urlRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const pick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const url = await uploadImage(file);
            onChange([...value, url]);
            toast.success("Imagen subida");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const addUrl = () => {
        const url = urlRef.current?.value?.trim();
        if (!url) return;
        onChange([...value, url]);
        urlRef.current.value = "";
    };

    return (
        <div>
            <label className="block text-sm font-semibold text-brand-ink">{label}</label>
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
                {value.map((url, i) => (
                    <div key={`${url}-${i}`} className="relative h-20 w-20 overflow-hidden rounded-xl bg-black/[0.03]">
                        <img src={url} alt={`Foto adicional ${i + 1}`} className="h-full w-full object-contain" />
                        <button
                            type="button"
                            data-testid={`${testId}-remove-${i}`}
                            onClick={() => onChange(value.filter((_, j) => j !== i))}
                            aria-label={`Quitar foto ${i + 1}`}
                            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-brand-ink/80 text-white transition-colors hover:bg-brand-magenta"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ))}
                <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
                <button
                    type="button"
                    data-testid={`${testId}-upload`}
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand-ink px-5 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-60"
                >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? "Subiendo..." : "Agregar foto"}
                </button>
            </div>
            <div className="mt-2 flex gap-2">
                <input ref={urlRef} data-testid={`${testId}-url`} type="url" placeholder="o pega una URL https://..." className={`${inputCls} text-sm`} />
                <button
                    type="button"
                    data-testid={`${testId}-add-url`}
                    onClick={addUrl}
                    className="min-h-[44px] shrink-0 rounded-full border border-black/15 px-5 text-sm font-semibold text-brand-ink transition-colors hover:bg-black/5"
                >
                    Agregar
                </button>
            </div>
        </div>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label className="block text-sm font-semibold text-brand-ink">{label}</label>
        <div className="mt-1.5">{children}</div>
    </div>
);

export const inputCls =
    "min-h-[44px] w-full rounded-xl border border-black/15 px-4 text-base outline-none transition-colors focus:border-brand-magenta bg-white";

const AdminModal = ({ title, onClose, onSubmit, saving, children, testId }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
        <form
            data-testid={testId}
            onSubmit={onSubmit}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-lift sm:p-8"
        >
            <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-bold text-brand-ink">{title}</h2>
                <button
                    type="button"
                    data-testid={`${testId}-close`}
                    onClick={onClose}
                    aria-label="Cerrar"
                    className="flex h-10 w-10 items-center justify-center rounded-full text-brand-muted hover:bg-black/5"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>
            <div className="mt-5 space-y-4">{children}</div>
            <div className="mt-6 flex gap-3">
                <button
                    type="button"
                    data-testid={`${testId}-cancel`}
                    onClick={onClose}
                    className="min-h-[44px] flex-1 rounded-full border border-black/15 font-display font-semibold text-brand-ink transition-colors hover:bg-black/5"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    data-testid={`${testId}-save`}
                    disabled={saving}
                    className="min-h-[44px] flex-1 rounded-full bg-brand-magenta font-display font-semibold text-white transition-colors hover:bg-brand-magentadark disabled:opacity-60"
                >
                    {saving ? "Guardando..." : "Guardar"}
                </button>
            </div>
        </form>
    </div>
);

export { Field, AdminModal };
export default AdminModal;
